import React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Box, Tooltip, Typography } from '@mui/material'
import { pathInView, useAdminView } from '../hook/useAdminView'

/*
 * The dashboard's "Classic view" pill (espy-dashboard Header.tsx,
 * LegacyUIToggleButton): a 36px translucent-blue pill, a switch that is ON
 * while the modern pages are showing, and the name of the view a click sends
 * you to. Switching moves you to the same page in the other view when it has
 * one (useAdminView.ts PAIRS); a page without a twin just stays.
 */
export function ViewToggle({ dark }: { dark?: boolean }) {
  const { view, setView } = useAdminView()
  const navigate = useNavigate()
  const { pathname, search } = useLocation()
  const modern = view === 'modern'
  const label = modern ? 'Classic view' : 'Modern view'

  const toggle = () => {
    const next = modern ? 'classic' : 'modern'
    setView(next)
    const target = pathInView(pathname, next)
    if (target) navigate(`${target}${search}`)
  }

  return (
    <Tooltip title={modern ? 'Switch to the classic admin pages' : 'Switch to the modern admin pages'}>
      <Box
        role="switch"
        aria-checked={modern}
        onClick={toggle}
        sx={{
          height: 36, display: 'inline-flex', alignItems: 'center', gap: '8px', flex: 'none',
          borderRadius: '999px', padding: '0 14px', cursor: 'pointer', whiteSpace: 'nowrap',
          border: `1px solid ${dark ? 'rgba(45,137,244,.24)' : '#DBE9FE'}`,
          background: dark ? 'rgba(45,137,244,.14)' : '#EEF5FF',
          userSelect: 'none',
        }}
      >
        <Box sx={{
          position: 'relative', width: 34, height: 20, borderRadius: '999px', flex: 'none',
          background: modern ? '#2D89F4' : (dark ? '#3A3872' : '#CBD5E1'),
          transition: 'background .15s',
        }}>
          <Box sx={{
            position: 'absolute', top: 2, left: modern ? 16 : 2, width: 16, height: 16, borderRadius: '50%',
            background: '#fff', boxShadow: '0 1px 3px rgba(15,23,42,.3)', transition: 'left .15s',
          }} />
        </Box>
        <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 13, fontWeight: 600, color: dark ? '#7CC2FF' : '#1B6FD6' }}>
          {label}
        </Typography>
      </Box>
    </Tooltip>
  )
}
